import React, { useEffect, useState, createContext, useContext } from "react";
import { auth, db } from "../../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot, setDoc, updateDoc, collection, query, where, getDocs, limit, serverTimestamp, getDoc } from "firebase/firestore";

const AuthContext = createContext({ user: null, rol: null, userStatus: null, loading: true, error: null });

export function useAuth() {
  return useContext(AuthContext) ?? { user: null, rol: null, userStatus: null, loading: true, error: null };
}

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [rol, setRol] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log("🔍 AuthProvider - Iniciando listener de autenticación");
    let unsubUser = null;
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      try {
        if (unsubUser) {
          unsubUser();
          unsubUser = null;
        }
        if (!db) {
          setUser(firebaseUser || null);
          setRol(null);
          setLoading(false);
          return;
        }
        if (firebaseUser) {
          setUser(firebaseUser);
          const userDocRef = doc(db, "usuarios", firebaseUser.uid);
          // Actualizar lastLoginAt y, si falta, primerInicioSesionAt (para que el agente vea "Inició sesión" / "Cambió clave")
          getDoc(userDocRef).then((snap) => {
            if (snap.exists()) {
              const data = snap.data();
              const updates = { lastLoginAt: serverTimestamp() };
              if (!data.primerInicioSesionAt && !data.primer_inicio_sesion_at) updates.primerInicioSesionAt = serverTimestamp();
              if (!data.passwordEstablecidaAt && !data.password_establecida_at) updates.passwordEstablecidaAt = serverTimestamp();
              updateDoc(userDocRef, updates).catch(() => {});
            }
          }).catch(() => {});
          unsubUser = onSnapshot(userDocRef, (docSnap) => {
            if (docSnap.exists()) {
              const userData = docSnap.data();
              setRol(userData.rol);
              const s = userData.status ?? userData.estado;
              const activo = userData.activo === true || s === 'activo' || s === 'activa';
              setUserStatus(activo ? 'activo' : (s || null));
              setLoading(false);
              return;
            }
            setUserStatus(null);
            console.log("❌ Usuario no encontrado en usuarios por UID, buscando por email...");
            const usuariosQuery = query(
              collection(db, "usuarios"),
              where("email", "==", firebaseUser.email),
              limit(1)
            );
            getDocs(usuariosQuery).then((usuariosSnapshot) => {
              if (!usuariosSnapshot.empty) {
                const userDoc = usuariosSnapshot.docs[0];
                const userData = userDoc.data();
                setRol(userData.rol);
                const s = userData.status ?? userData.estado;
                const activo = userData.activo === true || s === 'activo' || s === 'activa';
                setUserStatus(activo ? 'activo' : (s || null));
                setLoading(false);
                if (userDoc.id !== firebaseUser.uid) {
                  setDoc(
                    doc(db, "usuarios", firebaseUser.uid),
                    { rol: userData.rol, email: firebaseUser.email, lastLoginAt: serverTimestamp() },
                    { merge: true }
                  ).then(() => {}).catch((syncErr) => {
                    console.warn("⚠️ No se pudo sincronizar usuarios por UID:", syncErr.message);
                  });
                } else {
                  updateDoc(doc(db, "usuarios", firebaseUser.uid), { lastLoginAt: serverTimestamp() }).catch(() => {});
                }
              } else {
                setRol(null);
                setUserStatus(null);
                setLoading(false);
              }
            });
          }, () => {
            setRol(null);
            setUserStatus(null);
          });
        } else {
          console.log("❌ Usuario no logueado");
          setUser(null);
          setRol(null);
          setUserStatus(null);
          setLoading(false);
        }
      } catch (err) {
        console.error("❌ Error en AuthProvider:", err);
        setError(err.message);
        setLoading(false);
      }
    });
    return () => {
      if (unsubUser) unsubUser();
      unsub();
    };
  }, []);

  console.log("🔍 AuthProvider - Renderizando con estado:", {
    user: user,
    rol: rol,
    loading: loading,
    error: error
  });

  return (
    <AuthContext.Provider value={{ user, rol, userStatus, loading, error }}>
      {children}
    </AuthContext.Provider>
  );
}